/// <reference types="google.maps" />

import {markRaw} from 'vue';
import {gmAPI, MapService} from './Map';

/** @type {google.maps.InfoWindow} */
const _infoWindow = markRaw(new gmAPI.InfoWindow({}));
/** @type {Map<string, google.maps.MapsEventListener>} */
const _listeners = new Map();
const InfoWindow = {
  /**
   * Set the current info window content. This will immediately change the content regardless of the current state of
   * the info window.
   * @param {string|Element|Text} content Content for the Google Maps InfoWindow. Can be a string or a DOM element. If a
   * DOM element is supplied and that element exists in the DOM, it will be removed from wherever it is and placed in
   * the InfoWindow. If a ref to a Vue component is used, that component will NOT have a normal lifecycle. Instead,
   * the Google Maps API will suspend the component when the InfoWindow is closed, and resume it when the InfoWindow is
   * opened. None of the normal Vue lifecycle methods will be called.
   */
  setContent: function(content) {
    _infoWindow.setContent(content);
  },
  /**
   * Sets an event listener on the InfoWindow and returns the listener for later cancellation.
   * @param {string} listenerID ID of the listener for later cancellation
   * @param {string} eventName name of the event to listen for
   * @param {function(): void} handler function to call when event is triggered
   * @return {google.maps.MapsEventListener} listener for the event
   */
  addListener: function(listenerID, eventName, handler) {
    if (_listeners.has(listenerID)) {
      this.removeListener(listenerID);
    }
    const listener = _infoWindow.addListener(eventName, handler);
    _listeners.set(listenerID, listener);
    return listener;
  },
  /**
   * Remove a single listener from the InfoWindow
   * @param {string} listenerID ID of the listener to remove
   * @return {boolean} true if the listener was removed, false if not removed, or did not exist
   */
  removeListener: function(listenerID) {
    const listener = _listeners.get(listenerID);
    if (listener) {
      gmAPI.event.removeListener(listener);
      return _listeners.delete(listenerID);
    } else {
      return false;
    }
  },
  /**
   * Open the InfoWindow at the given marker's position
   * @param {google.maps.Marker|google.maps.LatLng|google.maps.LatLngLiteral} position
   * Info Window will be opened on this marker
   */
  open: function(position) {
    // if position is a marker, use it as the anchor
    if (position instanceof gmAPI.Marker) {
      _infoWindow.open({map: MapService.getMap(), anchor: position});
      return;
    }
    // Check if the position has a lat and lng and can be used as a LatLng or LatLngLiteral to set the position
    if (Object.hasOwn(position, 'lat') && Object.hasOwn(position, 'lng')) {
      _infoWindow.setPosition(position);
      _infoWindow.open({map: MapService.getMap()});
      return;
    }
    throw new Error('Invalid position passed to InfoWindow.open(): ' + JSON.stringify(position));
  },
  /**
   * Close the InfoWindow
   */
  close: function() {
    _infoWindow.close();
  },
};

export {
  InfoWindow,

  // Expose these for testing
  _infoWindow,
  _listeners,
};
